import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";
import QRCode from "qrcode";
import { useTranslation } from "react-i18next";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import payPaymentService, { stripeKey } from "~/services/payPaymentService";
import { getOneBookingTransaction } from "~/services/bookingTransactionService";
import checkoutServices from "~/services/checkoutServices";
import { Firebase } from "~/services/firebase";
import businessAction from "~/store/actions/businessAction";
import { useWhatsAppAuthContext } from "~/contexts/WhatsAppAuthContext";
import WhatsAppLoginButton from "~/components/common/whatsappLogin";
import Utils from "~/helpers/Utils";
import dayjs from "~/config/dayjs";
import SpinnerLoading from "~/components/common/spinnerLoading/SpinnerLoading";
import { mixpanelTrackEvent } from "~/utils/mixpanel";
import * as Sentry from "@sentry/react";

// ─── palette ───────────────────────────────────────────────────────────────────
const C = {
	wine: "#6b2030",
	winePress: "#581a27",
	cream: "#f7f0e9",
	peach1: "#efcab2",
	peach2: "#f3d9c8",
	ink: "#2c2420",
	muted: "#94897f",
	line: "#ece5dd",
	avatarBg: "#f7d3df",
	avatarIc: "#c2185b",
	red: "#d64b4b",
};

// ─── helpers ───────────────────────────────────────────────────────────────────
const maskCPF = (v) => {
	v = v.replace(/\D/g, "").slice(0, 11);
	if (v.length > 9) return v.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/, "$1.$2.$3-$4");
	if (v.length > 6) return v.replace(/(\d{3})(\d{3})(\d{1,3})/, "$1.$2.$3");
	if (v.length > 3) return v.replace(/(\d{3})(\d{1,3})/, "$1.$2");
	return v;
};

const maskCardNumber = (v) =>
	v.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();

const maskExpiry = (v) =>
	v.replace(/\D/g, "").slice(0, 4).replace(/(\d{2})(\d)/, "$1/$2");

const maskCVC = (v) => v.replace(/\D/g, "").slice(0, 4);

// cents → reais formatados com a moeda do negócio
const fmtMoney = (cents, currency = "BRL") =>
	Utils.formatMoney(cents / 100, currency);

const fmtDate = (dateStr) => {
	if (!dateStr) return "";
	return dayjs(dateStr).format("DD/MM [às] HH:mm");
};

const fmtDuration = (minutes) => {
	if (!minutes) return "";
	const h = Math.floor(minutes / 60);
	const m = minutes % 60;
	if (h && m) return `${h}h ${m}m`;
	if (h) return `${h}h`;
	return `${m}m`;
};

// ─── sub-components ────────────────────────────────────────────────────────────

const BackArrowIcon = () => (
	<svg width="22" height="22" viewBox="0 0 24 24" fill="none">
		<path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const ClockIcon = () => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none">
		<circle cx="12" cy="12" r="9" stroke={C.muted} strokeWidth="1.6" />
		<path d="M12 7v5l3 2" stroke={C.muted} strokeWidth="1.6" strokeLinecap="round" />
	</svg>
);

const CloseIcon = () => (
	<svg width="22" height="22" viewBox="0 0 24 24" fill="none">
		<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
	</svg>
);

const LockIcon = () => (
	<svg width="14" height="14" viewBox="0 0 24 24" fill="none">
		<rect x="5" y="11" width="14" height="9" rx="2" stroke={C.muted} strokeWidth="1.8" />
		<path d="M8 11V8a4 4 0 018 0v3" stroke={C.muted} strokeWidth="1.8" />
	</svg>
);

const CardIcon = () => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
		<rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
		<path d="M2 10h20" stroke="currentColor" strokeWidth="1.8" />
	</svg>
);

const InfoIcon = () => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
		<circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
		<path d="M12 8v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
		<circle cx="12" cy="16.3" r="1.1" fill="currentColor" />
	</svg>
);

const PersonIcon = () => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
		<circle cx="12" cy="8" r="4" fill={C.avatarIc} />
		<path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" fill={C.avatarIc} />
	</svg>
);

const CopyIcon = () => (
	<svg width="18" height="18" viewBox="0 0 18 18" fill="none">
		<rect x="5" y="5" width="10" height="11" rx="2" stroke="currentColor" strokeWidth="1.6" />
		<path d="M11.5 5V4a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
	</svg>
);

const TimerIcon = () => (
	<svg width="14" height="14" viewBox="0 0 24 24" fill="none">
		<circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
		<path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
	</svg>
);

const CheckIcon = () => (
	<svg width="40" height="40" viewBox="0 0 24 24" fill="none">
		<path d="M5 13l4 4 10-10" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

// ─── pix QR canvas (encodes o pix_qr_code retornado pelo pagamento) ───────────
const PIX_QR_SIZE = 96;
const PixQR = ({ value }) => {
	const ref = useRef(null);
	useEffect(() => {
		const c = ref.current;
		if (!c || !value) return;
		QRCode.toCanvas(c, value, { width: PIX_QR_SIZE * 2, margin: 1 }).catch(() => {});
	}, [value]);
	return <canvas ref={ref} style={{ width: PIX_QR_SIZE, height: PIX_QR_SIZE, display: "block", borderRadius: 4 }} />;
};

// ─── stripe payment form (pagamento internacional / não-BRL) ─────────────────
const StripePaymentForm = ({ returnUrl, onSuccess, onError }) => {
	const stripe = useStripe();
	const elements = useElements();
	const { t } = useTranslation();
	const [submitting, setSubmitting] = useState(false);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!stripe || !elements) return;
		setSubmitting(true);
		const { error } = await stripe.confirmPayment({
			elements,
			confirmParams: { return_url: returnUrl },
			redirect: "if_required",
		});
		setSubmitting(false);
		if (error) {
			onError(error.message);
		} else {
			onSuccess();
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<PaymentElement id="payment-element" />
			<button
				type="submit"
				disabled={submitting || !stripe || !elements}
				style={{ ...s.btnWine, ...((submitting || !stripe || !elements) ? { background: "#e2dad1", color: "#b3a99f", cursor: "default" } : {}) }}>
				{submitting ? t("whatsappAuthProcessing") : t("Confirm payment")}
			</button>
		</form>
	);
};

// ─── pix BR Code EMV (balcão) ─────────────────────────────────────────────────
// const crc16ccitt = (str) => {
// 	let crc = 0xffff;
// 	for (let i = 0; i < str.length; i++) {
// 		crc ^= str.charCodeAt(i) << 8;
// 		for (let j = 0; j < 8; j++) {
// 			crc = (crc & 0x8000) ? ((crc << 1) ^ 0x1021) : (crc << 1);
// 			crc &= 0xffff;
// 		}
// 	}
// 	return crc.toString(16).toUpperCase().padStart(4, "0");
// };

// const buildPixPayload = ({ pixKey, amount, merchantName, merchantCity, transactionId }) => {
// 	const tlv = (id, v) => `${id}${String(v.length).padStart(2, "0")}${v}`;
// 	const sanitize = (s, max) =>
// 		(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9 ]/g, " ").trim().substring(0, max) || "N/A";
// 	const txId = (transactionId || "").replace(/[^a-zA-Z0-9]/g, "").slice(0, 25) || "***";

// 	let payload = tlv("00", "01");
// 	payload += tlv("26", tlv("00", "br.gov.bcb.pix") + tlv("01", pixKey));
// 	payload += tlv("52", "0000");
// 	payload += tlv("53", "986");
// 	if (amount > 0) payload += tlv("54", Number(amount).toFixed(2));
// 	payload += tlv("58", "BR");
// 	payload += tlv("59", sanitize(merchantName, 25));
// 	payload += tlv("60", sanitize(merchantCity, 15));
// 	payload += tlv("62", tlv("05", txId));
// 	payload += "6304";
// 	return payload + crc16ccitt(payload);
// };

// ─── styles (shared objects) ───────────────────────────────────────────────────
const s = {
	btnWine: {
		width: "100%",
		height: 56,
		borderRadius: 999,
		background: C.wine,
		color: "#fff",
		fontSize: 17,
		fontWeight: 800,
		border: "none",
		cursor: "pointer",
		marginTop: 22,
		fontFamily: "inherit",
		transition: "filter .15s ease",
	},
	fieldLabel: {
		fontSize: 15,
		fontWeight: 700,
		color: C.ink,
		marginTop: 20,
		display: "block",
	},
	field: {
		marginTop: 8,
		border: `1.5px solid #e2dad1`,
		borderRadius: 13,
		padding: "15px 16px",
		fontSize: 17,
		color: C.ink,
		width: "100%",
		fontFamily: "inherit",
		outline: "none",
		background: "#fff",
		boxSizing: "border-box",
	},
	mTitle: {
		fontSize: 21,
		fontWeight: 800,
		color: C.ink,
	},
	mDesc: {
		fontSize: 16,
		color: "#5a524b",
		lineHeight: 1.45,
		marginTop: 10,
	},
};

// ─── main component ────────────────────────────────────────────────────────────
const LaterPaymentCheckout = (props) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const { id: businessId, bookingTransactionId } = useParams();
	const { business } = useSelector((store) => store.business);
	const { user } = useSelector((store) => store.auth);
	const meStore = useSelector((store) => store.me?.me);
	const me = Utils.safeParseJSON("@prit-web/meData", meStore);
	const { authStatus } = useWhatsAppAuthContext();
	const { t, i18n } = useTranslation();

	const searchParams = new URLSearchParams(location.search);
	const isAuthenticated = user && !user.isAnonymous;

	const typeParam = searchParams.get("type");
	const fallbackToBusinessLink = (motivo) => {
		const returnPath = `/later-payment/${businessId}/bookingTransactionId/${bookingTransactionId}${typeParam ? `?type=${typeParam}` : ""}`;
		history.push(`/business/link/${businessId}`, { goBackRoute: returnPath });
	};


	const locationState = props?.location?.state || {};

	// paymentType: vem do state (deep link) ou query param
	const paymentType = locationState.paymentType || searchParams.get("type");
	// const pixBusinessParams = locationState.pixBusiness || searchParams.get("pix_business");
	const isCardPritPay = paymentType === "pritpay_card" || !paymentType;
	// const isPixBalcao = paymentType === "pix_balcao";
	// const pixBusiness = pixBusinessParams || locationState.pixBusiness || null;

	// ── dados resolvidos: vêm do location.state (deep link) ou são buscados da API ──
	const [resolvedPostPayment, setResolvedPostPayment] = useState(locationState.postPayment || null);
	const [resolvedServices, setResolvedServices] = useState(locationState.services || []);
	const [isLoadingData, setIsLoadingData] = useState(!locationState.postPayment);

	// booking_external_id: usa o primeiro booking_transaction_id do payment_request quando disponível
	const bookingExternalId = resolvedPostPayment?.booking_external_id || bookingTransactionId;

	const [alreadyPaid, setAlreadyPaid] = useState(false);

	// ── garante que o business seja carregado independente do estado de auth ──
	useEffect(() => {
		if (businessId) {
			dispatch(businessAction.getBusiness(businessId));
		}
	}, [businessId]);

	// Atualizar traducao do business
	useEffect(() => {
		if (!business?.language || !i18n?.changeLanguage) return;
		if (business.language === "portuguese" || business.language === "portugal") {
			i18n.changeLanguage("pt-BR");
		} else if (business.language === "spanish") {
			i18n.changeLanguage("es-ES");
		} else if (business.language === "english") {
			i18n.changeLanguage("en-US");
		}
	}, [business?.language, i18n]); 

	// ── busca dados da API quando não veio pelo deep link ───────────────────
	useEffect(() => {
		// Aguarda o Firebase restaurar a sessão (user === null = ainda não resolvido)
		if (user === null) return;

		let cancelled = false;

		const runWhenReady = async () => {
			// Aguarda useAuthCodeFlow do App.jsx concluir.
			// Não retorna cedo: o useEffect pode disparar antes do finally do hook
			// (onIdTokenChanged chega antes que __PRIT_CODE_LOGIN_IN_PROGRESS__ seja limpa),
			// e sem isso nenhuma dependência muda depois → loading infinito.
			if (window.__PRIT_CODE_LOGIN_IN_PROGRESS__) {
				await new Promise((resolve) => {
					const interval = setInterval(() => {
						if (!window.__PRIT_CODE_LOGIN_IN_PROGRESS__) {
							clearInterval(interval);
							resolve();
						}
					}, 50);
				});
			}

			if (cancelled) return;

			// Lê o usuário diretamente do Firebase após o fluxo de auth concluir,
			// evitando dependência de timing no Redux.
			const currentUser = Firebase.currentUser ?? user;
			const isCurrentlyAuthenticated = currentUser && !currentUser.isAnonymous;

			// Sem autenticação → redireciona para login
			if (!isCurrentlyAuthenticated) {
				fallbackToBusinessLink('sem_autenticacao');
				return;
			}

			setShowLoginModal(false);

			// Se já tem os dados do location.state, não precisa buscar
			if (locationState.postPayment) {
				setIsLoadingData(false);
				return;
			}

			const fetchData = async () => {
			setIsLoadingData(true);
			try {
				const userId = localStorage.getItem("@prit-web/userId");
				const meData = Utils.safeParseJSON("@prit-web/meData", null);
				const currentUserId = userId || meData?.id || me?.id;

				// 1. Busca payment requests do cliente
				let paymentRequest = null;
				if (currentUserId) {
					const payResponse = await checkoutServices.fetchPaymentRequest(currentUserId);
					const payments = payResponse?.data || [];

					paymentRequest = payments.find((p) =>
						p.booking_transaction_references?.some(
							(ref) => String(ref.booking_transaction_id) === String(bookingTransactionId)
						)
					);
				}

				if (paymentRequest) {
					// 2. Busca todos os booking_transactions do payment_request em paralelo
					const btRefs = paymentRequest.booking_transaction_references || [];
					const bookingResponses = await Promise.all(
						btRefs.map((ref) => getOneBookingTransaction(ref.booking_transaction_id))
					);

					// 3. Verifica se algum já foi pago
					const anyCompleted = bookingResponses.some(
						(res) => res?.data?.paymentStatus === "completed"
					);
					if (anyCompleted) setAlreadyPaid(true);

					// 4. Agrega serviços de todos os booking_transactions
					const allServices = [];
					bookingResponses.forEach((res) => {
						const bookingData = res?.data;
						if (!bookingData) return;
						(bookingData?.bookings || []).forEach((booking) => {
							allServices.push({
								date: booking?.startTime,
								dateStart: booking?.startTime,
								professional: {
									avatarUrl: booking?.professional?.avatarUrl || null,
									duration: booking?.duration,
									id: booking?.professional?.id,
									name: booking?.professional?.name || "Profissional",
									price: booking?.price,
									pricingType: booking?.pricingType?.name || booking?.pricingType,
								},
								professional_id: booking?.professional?.id,
								service: {
									serviceDuration: booking?.duration,
									serviceName: booking?.service?.name,
									servicePrice: booking?.price,
								},
								service_id: booking?.service?.id,
							});
						});
					});

					setResolvedServices(allServices);

					// 5. booking_external_id = primeiro booking_transaction_id do payment_request
					const firstBookingTransactionId = btRefs[0]?.booking_transaction_id || bookingTransactionId;
					const prRecipientId = paymentRequest.recipient_id || businessId;

					let checkoutInformation = null;
					try {
						const anticipatedRes = await checkoutServices.fetchPaymentAnticipatedAmountByBookTransaction({
							recipient_id: prRecipientId,
							payment_request_id: paymentRequest.payment_request_id,
						});
						checkoutInformation = anticipatedRes?.data || null;
					} catch (_) {}

					setResolvedPostPayment({
						booking_external_id: firstBookingTransactionId,
						recipient_id: prRecipientId,
						payment_request_id: paymentRequest.payment_request_id,
						amount: paymentRequest.amount - (paymentRequest?.discount || 0),
						checkoutInformation,
					});
				} else {
					// Fallback: sem payment_request encontrado → busca booking_transaction direto
					const btResponse = await getOneBookingTransaction(bookingTransactionId);
					const bookingData = btResponse?.data;

					if (!bookingData) {
						setIsLoadingData(false);
						return;
					}

					if (bookingData.paymentStatus === "completed") setAlreadyPaid(true);

					const formattedServices = (bookingData?.bookings || []).map((booking) => ({
						date: booking?.startTime,
						dateStart: booking?.startTime,
						professional: {
							avatarUrl: booking?.professional?.avatarUrl || null,
							duration: booking?.duration,
							id: booking?.professional?.id,
							name: booking?.professional?.name || "Profissional",
							price: booking?.price,
							pricingType: booking?.pricingType?.name || booking?.pricingType,
						},
						professional_id: booking?.professional?.id,
						service: {
							serviceDuration: booking?.duration,
							serviceName: booking?.service?.name,
							servicePrice: booking?.price,
						},
						service_id: booking?.service?.id,
					}));

					setResolvedServices(formattedServices);

					const recipientId = bookingData?.recipientId || businessId;
					setResolvedPostPayment({
						booking_external_id: bookingTransactionId,
						recipient_id: recipientId,
						checkoutInformation: {
							total_value_for_services: formattedServices[0]?.service?.servicePrice || 0,
							total_discounts: 0,
							anticipation_payment: formattedServices[0]?.service?.servicePrice || 0,
						},
					});
				}
			} catch (err) {
				if (err?.response?.status !== 403) {
					Sentry.withScope((scope) => {
						scope.setTag('flow', 'laterPayment_fetchData');
						scope.setExtra('businessId', businessId);
						scope.setExtra('bookingTransactionId', bookingTransactionId);
						scope.setExtra('errorMessage', err?.message);
						scope.setExtra('errorStatus', err?.response?.status);
						Sentry.captureException(err);
					});
				}
				if (err?.response?.status === 403) {
					const typeParam = searchParams.get("type");
					const returnPath = `/later-payment/${businessId}/bookingTransactionId/${bookingTransactionId}${typeParam ? `?type=${typeParam}` : ""}`;
					history.push(`/business/link/${businessId}`, { goBackRoute: returnPath });
					return;
				}
			} finally {
				setIsLoadingData(false);
			}
		};

			if (!cancelled) setTimeout(fetchData, 300);
		};

		runWhenReady();
		return () => { cancelled = true; };

	}, [bookingTransactionId, businessId, user?.uid]);

	// ── derived display data ─────────────────────────────────────────────────
	const currency = business?.currency || "BRL";
	const firstService = resolvedServices[0] || {};

	// valores em centavos
	const servicePrice = resolvedPostPayment?.amount != null
		? fmtMoney(resolvedPostPayment.amount, currency)
		: resolvedPostPayment?.checkoutInformation?.total_value_for_services
		? fmtMoney(resolvedPostPayment.checkoutInformation.total_value_for_services, currency)
		: firstService?.service?.servicePrice != null
		? fmtMoney(firstService.service.servicePrice, currency)
		: "";
	const anticipationAmount = servicePrice;
	const businessName = business?.name || "";

	// ── ui state ─────────────────────────────────────────────────────────────
	// steps: initial | cpf | confirm | pix | card | done
	const [step, setStep] = useState("initial");
	const [cpf, setCpf] = useState("");
	const [cpfError, setCpfError] = useState(false);
	const [pixCode, setPixCode] = useState("");
	const [pixBalcaoCode, setPixBalcaoCode] = useState("");
	const [loading, setLoading] = useState(false);
	const [payError, setPayError] = useState("");
	const [countdown, setCountdown] = useState(40 * 60);
	const [toastMsg, setToastMsg] = useState("");
	const [copied, setCopied] = useState(false);
	const [showLoginModal, setShowLoginModal] = useState(false);
	const [pendingStep, setPendingStep] = useState(null);

	// ── card state ───────────────────────────────────────────────────────────
	const [cardForm, setCardForm] = useState({ number: "", expiry: "", cvc: "", name: "", cpf: "", email: "" });
	const [cardErrors, setCardErrors] = useState({});

	// ── stripe state (pagamento internacional / não-BRL) ─────────────────────
	const [clientSecret, setClientSecret] = useState(null);
	const [stripePromise, setStripePromise] = useState(null);

	const pollingRef = useRef(null);
	const cdRef = useRef(null);

	// ── cleanup on unmount ───────────────────────────────────────────────────
	useEffect(() => () => {
		clearInterval(pollingRef.current);
		clearInterval(cdRef.current);
	}, []);
	
	// ── proceed after WhatsApp login ─────────────────────────────────────────
	useEffect(() => {
		if (authStatus === "SUCCESS") {
			if (pendingStep) {
				setStep(pendingStep);
				setPendingStep(null);
			}
			setShowLoginModal(false);
		}
	}, [authStatus, pendingStep]);

	// ── countdown ────────────────────────────────────────────────────────────
	useEffect(() => {
		if (step !== "pix") return;
		setCountdown(40 * 60);
		cdRef.current = setInterval(() => setCountdown((t) => (t > 0 ? t - 1 : 0)), 1000);
		return () => clearInterval(cdRef.current);
	}, [step]);

	const countdownLabel = () => {
		const m = Math.floor(countdown / 60);
		const s = countdown % 60;
		return t("Expires in {{time}}", { time: `${m}:${String(s).padStart(2, "0")}` });
	};

	// ── toast ────────────────────────────────────────────────────────────────
	const showToast = (msg) => {
		setToastMsg(msg);
		setTimeout(() => setToastMsg(""), 1800);
	};

	const handlePaymentClick = (target) => {
		setPayError("");
		if (isAuthenticated || authStatus === "SUCCESS") {
			setStep(target);
		} else {
			setPendingStep(target);
			setShowLoginModal(true);
		}
	};

	// ── cpf handling ─────────────────────────────────────────────────────────
	const handleCpfChange = (e) => {
		setCpf(maskCPF(e.target.value));
		setCpfError(false);
	};

	const handleCpfContinue = () => {
		if (cpf.replace(/\D/g, "").length !== 11) {
			setCpfError(true);
			return;
		}
		setStep("confirm");
	};

	// ── pix balcão: gera QR localmente, sem chamada de API ───────────────────
	// const handlePixBalcao = () => {
	// 	try {
	// 		const rawAmount = resolvedPostPayment?.amount ?? resolvedPostPayment?.checkoutInformation?.anticipation_payment ?? 0;
	// 		const code = buildPixPayload({
	// 			pixKey: pixBusiness,
	// 			amount: rawAmount / 100,
	// 			merchantName: business?.name || "Estabelecimento",
	// 			merchantCity: business?.address?.city || "Brasil",
	// 			transactionId: bookingExternalId,
	// 		});
	// 		setPixBalcaoCode(code);
	// 		setStep("pix_balcao");
	// 	} catch (e) {
	// 		setPayError("Erro ao gerar código Pix");
	// 	}
	// };

	// ── monta (de forma sequencial/síncrona) os dados de pagamento antes de enviar ──
	// Revalida o payment_request na hora de pagar em vez de confiar apenas no que
	// foi resolvido no carregamento da página: o payment_request pode ter sido
	// criado no backend depois desse carregamento inicial, e sem ele o pagamento
	// não é corretamente correlacionado à cobrança original.
	const resolvePaymentInfo = async () => {
		if (resolvedPostPayment?.payment_request_id) {
			return resolvedPostPayment;
		}

		const userId = localStorage.getItem("@prit-web/userId");
		const meData = Utils.safeParseJSON("@prit-web/meData", null);
		const currentUserId = userId || meData?.id || me?.id;
		if (!currentUserId) return resolvedPostPayment;

		const payResponse = await checkoutServices.fetchPaymentRequest(currentUserId);
		const payments = payResponse?.data || [];
		const paymentRequest = payments.find((p) =>
			p.booking_transaction_references?.some(
				(ref) => String(ref.booking_transaction_id) === String(bookingExternalId)
			)
		);

		if (!paymentRequest) {
			console.warn("LaterPaymentCheckout: nenhum payment_request encontrado ao montar o pagamento", bookingExternalId, "payments recebidos:", payments);
			return resolvedPostPayment;
		}

		const recipientId = paymentRequest.recipient_id || resolvedPostPayment?.recipient_id || businessId;
		let checkoutInformation = resolvedPostPayment?.checkoutInformation || null;
		try {
			const anticipatedRes = await checkoutServices.fetchPaymentAnticipatedAmountByBookTransaction({
				recipient_id: recipientId,
				payment_request_id: paymentRequest.payment_request_id,
			});
			checkoutInformation = anticipatedRes?.data || checkoutInformation;
		} catch (_) {}

		const firstBtId = paymentRequest.booking_transaction_references?.[0]?.booking_transaction_id || bookingExternalId;
		const updated = {
			...resolvedPostPayment,
			booking_external_id: firstBtId,
			recipient_id: recipientId,
			payment_request_id: paymentRequest.payment_request_id,
			amount: paymentRequest.amount - (paymentRequest?.discount || 0),
			checkoutInformation,
		};
		setResolvedPostPayment(updated);
		return updated;
	};

	// ── payment submission ───────────────────────────────────────────────────
	const handleGeneratePix = async () => {
		if (!resolvedPostPayment || isLoadingData) return;
		setLoading(true);
		setPayError("");

		const userId = localStorage.getItem("@prit-web/userId");
		const meData = Utils.safeParseJSON("@prit-web/meData", null);
		const currentMe = me || meData || {};

		try {
			const paymentInfo = await resolvePaymentInfo();
			const addr = business?.address || {};

			const rawAmount = paymentInfo.amount ?? paymentInfo?.checkoutInformation?.anticipation_payment ?? 0;
			
			const body = {
				booking_external_id: paymentInfo.booking_external_id || bookingExternalId,
				recipient_id: paymentInfo.recipient_id || businessId,
				payment_request_id: paymentInfo.payment_request_id,
				payment_type: 5,
				client: {
					id: userId || currentMe?.id,
					name: currentMe?.name || user?.displayName || "Usuário Prit",
					email: currentMe?.email || user?.email || "contato@prit.app",
					country: "br",
					documents: [cpf.replace(/\D/g, "")],
					phone_numbers: currentMe?.phone
						? [currentMe.phone]
						: currentMe?.phoneUnverified
						? [currentMe.phoneUnverified]
						: ["+551640420575"],
					address: {
						street: addr.street || "Av. Prof. João Fiúsa",
						complementary: addr.unit || "",
						street_number: addr.number || "1901",
						neighborhood: addr.neighborhood || "Jardim Botânico",
						city: addr.city || "Ribeirão Preto",
						state: addr.state || "SP",
						zipcode: addr.postalCode || "14024250",
						country: "br",
					},
				},
				amount: rawAmount / 100,
				discount: (paymentInfo.checkoutInformation?.total_discounts || 0) / 100,
			};

			const res = await payPaymentService.postAdvancedPaymentPay(body);
			const code = res?.data?.pix_qr_code || res?.data?.code || "";
			setPixCode(code);
			setStep("pix");
			startPolling();
			mixpanelTrackEvent('Pagamento_Posterior_Confirmado', {
				'Business_Id': businessId,
				'BookingTransaction_Id': bookingTransactionId,
				'Amount': paymentInfo?.amount,
				'Tipo_Pagamento': 'pix',
			}, {}, business || {});
		} catch (err) {
			const msg = err?.response?.data?.message || err?.message || t("Error generating Pix code");
			setPayError(msg);
			mixpanelTrackEvent('Pagamento_Posterior_Erro', {
				'Business_Id': businessId,
				'BookingTransaction_Id': bookingTransactionId,
				'Metodo_Pagamento': 'pix',
				'Mensagem_Erro': msg,
			}, {}, business || {});
		} finally {
			setLoading(false);
		}
	};

	// ── polling ──────────────────────────────────────────────────────────────
	const startPolling = () => {
		clearInterval(pollingRef.current);
		pollingRef.current = setInterval(async () => {
			try {
				const res = await getOneBookingTransaction(bookingExternalId);
				const status = res?.data?.paymentStatus;
				if (status === "partial" || status === "completed") {
					clearInterval(pollingRef.current);
					clearInterval(cdRef.current);
					setStep("done");
				}
			} catch (_) {}
		}, 5000);
	};

	// ── card validation ───────────────────────────────────────────────────────
	const validateCardForm = () => {
		const errs = {};
		const raw = cardForm.number.replace(/\D/g, "");
		if (raw.length < 13) errs.number = t("Invalid card number");
		if (cardForm.expiry.replace(/\D/g, "").length < 4) errs.expiry = t("Invalid expiry");
		if (cardForm.cvc.length < 3) errs.cvc = t("Invalid CVV");
		if (!cardForm.name.trim()) errs.name = t("Enter your name");
		if (cardForm.cpf.replace(/\D/g, "").length !== 11) errs.cpf = t("Invalid CPF");
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cardForm.email.trim())) errs.email = t("Invalid email");
		setCardErrors(errs);
		return Object.keys(errs).length === 0;
	};

	// ── card submission ───────────────────────────────────────────────────────
	const handleSubmitCard = async () => {
		if (!validateCardForm() || !resolvedPostPayment) return;
		setLoading(true);
		setPayError("");

		const userId = localStorage.getItem("@prit-web/userId");
		const meData = Utils.safeParseJSON("@prit-web/meData", null);
		const currentMe = me || meData || {};

		try {
			const paymentInfo = await resolvePaymentInfo();

			const addr = business?.address || {};

			const rawAmount = paymentInfo.amount ?? paymentInfo?.checkoutInformation?.anticipation_payment ?? 0;

			const body = {
				booking_external_id: paymentInfo.booking_external_id || bookingExternalId,
				recipient_id: paymentInfo.recipient_id || businessId,
				payment_request_id: paymentInfo.payment_request_id,
				card: {
					card_holder_name: cardForm.name,
					card_expiration_date: cardForm.expiry.replace(/\D/g, ""),
					card_number: cardForm.number.replace(/\D/g, ""),
					card_cvv: cardForm.cvc,
				},
				client: {
					id: userId || currentMe?.id,
					name: cardForm.name || currentMe?.name || "Usuário Prit",
					email: cardForm.email || currentMe?.email || "contato@prit.app",
					country: "br",
					documents: [cardForm.cpf.replace(/\D/g, "")],
					phone_numbers: currentMe?.phone
						? [currentMe.phone]
						: currentMe?.phoneUnverified
						? [currentMe.phoneUnverified]
						: ["+551640420575"],
					address: {
						street: addr.street || "Av. Prof. João Fiúsa",
						complementary: addr.unit || "",
						street_number: addr.number || "1901",
						neighborhood: addr.neighborhood || "Jardim Botânico",
						city: addr.city || "Ribeirão Preto",
						state: addr.state || "SP",
						zipcode: addr.postalCode || "14024250",
						country: "br",
					},
				},
				amount: rawAmount / 100,
				discount: (paymentInfo.checkoutInformation?.total_discounts || 0) / 100,
			};

			if (currency !== "BRL") {
				const res = await payPaymentService.postAdvancedPayIntent(body);
				setClientSecret(res?.data?.payment_intent_client_secret);
				setStripePromise(loadStripe(stripeKey, { stripeAccount: res?.data?.connect_account_id }));
				setStep("stripe");
			} else {
				await payPaymentService.postAdvancedPaymentPay(body);
				setStep("done");
				mixpanelTrackEvent('Pagamento_Posterior_Confirmado', {
					'Business_Id': businessId,
					'BookingTransaction_Id': bookingTransactionId,
					'Amount': paymentInfo?.amount,
					'Currency': currency,
					'Tipo_Pagamento': 'cartao',
				}, {}, business || {});
			}
		} catch (err) {
			const msg = err?.response?.data?.message || err?.message || t("Error processing payment");
			setPayError(msg);
			mixpanelTrackEvent('Pagamento_Posterior_Erro', {
				'Business_Id': businessId,
				'BookingTransaction_Id': bookingTransactionId,
				'Metodo_Pagamento': 'cartao',
				'Mensagem_Erro': msg,
			}, {}, business || {});
		} finally {
			setLoading(false);
		}
	};

	// ── render ───────────────────────────────────────────────────────────────
	if (isLoadingData && !showLoginModal) return <SpinnerLoading loading />;

	return (
		<div style={{ width: "100%", height: "100dvh", position: "relative", background: C.cream, overflow: "hidden", fontFamily: '-apple-system,"SF Pro Display","SF Pro Text",BlinkMacSystemFont,"Segoe UI",system-ui,sans-serif' }}>

			{/* ── Main scroll area ── */}
			<div style={{ position: "absolute", inset: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 120 }}>
				{/* Hero */}
				<div style={{ background: `linear-gradient(180deg,${C.peach1},${C.peach2})`, padding: "70px 24px 74px", position: "relative" }}>
					<div style={{ fontSize: 38, lineHeight: 1.12, fontWeight: 800, color: C.ink, letterSpacing: -1 }}>
						{t("Complete your payment")}
					</div>
				</div>

				{/* Card */}
				<div style={{ background: "#fff", borderRadius: 24, margin: "-34px 16px 0", padding: "22px 22px 24px", boxShadow: "0 8px 30px rgba(80,50,30,.10)", position: "relative", zIndex: 2 }}>
					{businessName && (
						<div style={{ fontSize: 20, fontWeight: 800, color: C.ink, textAlign: "center", lineHeight: 1.25 }}>{businessName}</div>
					)}
					<div style={{ height: 1, background: C.line, margin: "18px 0" }} />

					{/* Lista de serviços */}
					{resolvedServices.map((svc, i) => (
						<div key={i}>
							{i > 0 && <div style={{ height: 1, background: C.line, margin: "16px 0" }} />}
							<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
								<div style={{ fontSize: 17, fontWeight: 700, color: C.ink, flex: 1, paddingRight: 8 }}>{svc.service?.serviceName}</div>
								<div style={{ fontSize: 16, fontWeight: 700, color: C.ink, flexShrink: 0 }}>
									{svc.service?.servicePrice != null ? fmtMoney(svc.service.servicePrice, currency) : ""}
								</div>
							</div>
							{svc.professional?.name && (
								<div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
									<div style={{ width: 44, height: 44, borderRadius: 999, background: C.avatarBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
										<PersonIcon />
									</div>
									<div>
										<div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>{svc.professional.name}</div>
										<div style={{ fontSize: 14, color: C.muted, marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}>
											{svc.date && fmtDate(svc.date)}
											{svc.professional?.duration && (
												<>
													<span>·</span>
													<ClockIcon />
													{fmtDuration(svc.professional.duration)}
												</>
											)}
										</div>
									</div>
								</div>
							)}
						</div>
					))}

					<div style={{ height: 1, background: C.line, margin: "18px 0" }} />

					{/* Total */}
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 19, fontWeight: 700, color: C.ink }}>
							{t("Total")}
						</div>
						<div style={{ fontSize: 20, fontWeight: 800, color: C.ink }}>{anticipationAmount || servicePrice}</div>
					</div>
				</div>
			</div>

			{/* ── CTA ── */}
			<div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "16px 16px 18px", background: `linear-gradient(180deg,rgba(247,240,233,0),${C.cream} 28%)`, zIndex: 40 }}>
				{ isCardPritPay ? (
					<div style={{ display: "flex", gap: 10 }}>
						<button
							onClick={() => handlePaymentClick("card")}
							style={{ ...s.btnWine, flex: 1, height: 62, fontSize: 16, marginTop: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "#fff", color: C.wine, border: `1.5px solid ${C.wine}` }}>
							<CardIcon />
							{t("Card")}
						</button>
						<button
							onClick={() => handlePaymentClick("cpf")}
							style={{ ...s.btnWine, flex: 1, height: 62, fontSize: 16, marginTop: 0 }}>
							{t("Pix")}
						</button>
					</div>
				) : (
					<button
						onClick={() => handlePaymentClick("cpf")}
						style={{ ...s.btnWine, height: 62, fontSize: 18, marginTop: 0 }}>
						{t("Pay with Pix")}
					</button>
				)}
			</div>

			{/* ── WhatsApp Login Modal (triggered when unauthenticated user tries to pay) ── */}
			{showLoginModal && (
				<WhatsAppLoginButton
					showCustomLogin={true}
					authContext="payment"
					checkoutInformation={resolvedPostPayment?.checkoutInformation || {}}
					business={business}
					onClose={() => {
						setShowLoginModal(false);
						setPendingStep(null);
					}}
				/>
			)}

			{/* ── Scrim ── */}
			{(step === "cpf" || step === "confirm") && (
				<div
					onClick={() => setStep("initial")}
					style={{ position: "absolute", inset: 0, background: "rgba(40,28,22,.5)", zIndex: 90 }}
				/>
			)}

			{/* ── Modal CPF ── */}
			{step === "cpf" && (
				<Modal onClose={() => setStep("initial")}>
					<div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
						<div style={s.mTitle}>{t("Pay with Pix")}</div>
						<button onClick={() => setStep("initial")} style={{ background: "none", border: "none", cursor: "pointer", color: "#a59a90", padding: 0, display: "flex" }}>
							<CloseIcon />
						</button>
					</div>
					<div style={s.mDesc}>{t("Enter your CPF to generate the payment code")}</div>

					<label style={s.fieldLabel}>CPF</label>
					<input
						autoFocus
						inputMode="numeric"
						placeholder="000.000.000-00"
						value={cpf}
						onChange={handleCpfChange}
						style={{ ...s.field, borderColor: cpfError ? C.red : "#e2dad1" }}
					/>

					{cpfError && (
						<div style={{ color: C.red, fontSize: 13, marginTop: 6 }}>{t("Invalid CPF")}</div>
					)}

					<div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: C.muted, marginTop: 11 }}>
						<LockIcon />
						{t("Used only to identify this payment")}
					</div>

					<button
						onClick={handleCpfContinue}
						disabled={cpf.replace(/\D/g, "").length !== 11}
						style={{ ...s.btnWine, ...(cpf.replace(/\D/g, "").length !== 11 ? { background: "#e2dad1", color: "#b3a99f", cursor: "default" } : {}) }}>
						{t("continue")}
					</button>
				</Modal>
			)}

			{/* ── Modal Confirmar ── */}
			{step === "confirm" && (
				<Modal onClose={() => setStep("initial")}>
					<div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
						<div style={s.mTitle}>{t("Confirm payment")}</div>
						<button onClick={() => setStep("initial")} style={{ background: "none", border: "none", cursor: "pointer", color: "#a59a90", padding: 0, display: "flex" }}>
							<CloseIcon />
						</button>
					</div>

					{/* Summary box */}
					<div style={{ textAlign: "center", marginTop: 8, padding: 18, background: "#faf5f0", borderRadius: 16 }}>
						<div style={{ fontSize: 14, fontWeight: 600, color: "#6a605a" }}>{businessName}</div>
						{resolvedServices.length > 0 && (
							<div style={{ marginTop: 10, textAlign: "left", display: "flex", flexDirection: "column", gap: 4 }}>
								{resolvedServices.map((svc, i) => (
									<div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#6a605a" }}>
										<span>{svc.service?.serviceName}</span>
										<span style={{ fontWeight: 600 }}>
											{svc.service?.servicePrice != null ? fmtMoney(svc.service.servicePrice, currency) : ""}
										</span>
									</div>
								))}
							</div>
						)}
						<div style={{ fontSize: 13, color: C.muted, marginTop: 12 }}>{t("Amount to pay now")}</div>
						<div style={{ fontSize: 34, fontWeight: 800, color: C.ink, letterSpacing: "-.6px", marginTop: 2 }}>{anticipationAmount || servicePrice}</div>
					</div>

					{/* Cancellation note */}
					{resolvedPostPayment?.checkoutInformation?.cancellation_policy && (
						<div style={{ display: "flex", gap: 11, background: "#fdf1ef", border: "1px solid #f4d9d4", borderRadius: 14, padding: "14px 15px", marginTop: 16, textAlign: "left" }}>
							<div style={{ color: "#c0392b", flexShrink: 0, marginTop: 1 }}>
								<InfoIcon />
							</div>
							<div>
								<div style={{ fontSize: 14, fontWeight: 800, color: "#a93226" }}>{t("Cancellation policy")}</div>
								<div style={{ fontSize: 13.5, color: "#955047", lineHeight: 1.4, marginTop: 3 }}>
									{t("Cancellation Policy Condition without refund")}
								</div>
							</div>
						</div>
					)}

					{/* CPF row */}
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, padding: "13px 16px", background: "#faf5f0", borderRadius: 13 }}>
						<div>
							<div style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>CPF</div>
							<div style={{ fontSize: 16, fontWeight: 700, color: C.ink, marginTop: 2 }}>{cpf}</div>
						</div>
						<button
							onClick={() => setStep("cpf")}
							style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, fontWeight: 700, color: C.wine, fontFamily: "inherit" }}>
							{t("Alterar")}
						</button>
					</div>

					{payError && (
						<div style={{ color: C.red, fontSize: 13, marginTop: 12, textAlign: "center" }}>{payError}</div>
					)}

					<button
						onClick={handleGeneratePix}
						disabled={loading || isLoadingData}
						style={{ ...s.btnWine, ...((loading || isLoadingData) ? { background: "#e2dad1", color: "#b3a99f", cursor: "default" } : {}) }}>
						{(loading || isLoadingData) ? t("Generating") : t("Generate Pix code")}
					</button>
				</Modal>
			)}

			{/* ── Full-screen Cartão ── */}
			<FullScreen open={step === "card"}>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px 6px" }}>
					<button
						onClick={() => { setPayError(""); setStep("initial"); }}
						style={{ width: 40, height: 40, borderRadius: 999, background: "#fff", border: `1px solid ${C.line}`, display: "flex", alignItems: "center", justifyContent: "center", color: "#7a716a", cursor: "pointer" }}>
						<CloseIcon />
					</button>
					<div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>{t("Pay with card")}</div>
					<div style={{ width: 40 }} />
				</div>

				<div style={{ padding: "8px 24px 48px", overflowY: "auto" }}>
					{/* Resumo */}
					<div style={{ textAlign: "center", padding: "12px 0 20px" }}>
						<div style={{ fontSize: 14, color: C.muted }}>{businessName}</div>
						<div style={{ fontSize: 38, fontWeight: 800, color: C.ink, letterSpacing: -1, marginTop: 4 }}>{anticipationAmount || servicePrice}</div>
					</div>

					{/* Campos */}
					{[
						{ key: "number",  label: t("Card number"),            placeholder: "0000 0000 0000 0000", inputMode: "numeric",   mask: maskCardNumber },
						{ key: "expiry",  label: t("Validity"),               placeholder: "MM/AA",               inputMode: "numeric",   mask: maskExpiry },
						{ key: "cvc",     label: t("CVV"),                    placeholder: "123",                 inputMode: "numeric",   mask: maskCVC },
						{ key: "name",    label: t("Name printed on card"),   placeholder: "NOME SOBRENOME",      inputMode: "text",      mask: (v) => v },
						{ key: "cpf",     label: t("Cardholder CPF"),         placeholder: "000.000.000-00",      inputMode: "numeric",   mask: maskCPF },
						{ key: "email",   label: t("Email used to pay"),      placeholder: "seu@email.com",       inputMode: "email",     mask: (v) => v },
					].map(({ key, label, placeholder, inputMode, mask }) => (
						<div key={key}>
							<label style={{ ...s.fieldLabel, marginTop: key === "number" ? 0 : 16 }}>{label}</label>
							<input
								inputMode={inputMode}
								placeholder={placeholder}
								value={cardForm[key]}
								onChange={(e) => {
									const nextValue = mask(e.target.value);
									setCardForm((prev) => ({ ...prev, [key]: nextValue }));
									setCardErrors((prev) => ({ ...prev, [key]: undefined }));
								}}
								style={{ ...s.field, borderColor: cardErrors[key] ? C.red : "#e2dad1" }}
							/>
							{cardErrors[key] && (
								<div style={{ color: C.red, fontSize: 13, marginTop: 4 }}>{cardErrors[key]}</div>
							)}
						</div>
					))}

					{payError && (
						<div style={{ color: C.red, fontSize: 13, marginTop: 16, textAlign: "center" }}>{payError}</div>
					)}

					<button
						onClick={handleSubmitCard}
						disabled={loading}
						style={{ ...s.btnWine, ...(loading ? { background: "#e2dad1", color: "#b3a99f", cursor: "default" } : {}), marginTop: 28 }}>
						{loading ? t("whatsappAuthProcessing") : t("Confirm payment")}
					</button>

					<div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: C.muted, marginTop: 14, justifyContent: "center" }}>
						<LockIcon />
						{t("Secure and encrypted payment")}
					</div>
				</div>
			</FullScreen>

			{/* ── Full-screen Stripe (pagamento internacional / não-BRL) ── */}
			<FullScreen open={step === "stripe"}>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px 6px" }}>
					<button
						onClick={() => { setPayError(""); setStep("initial"); }}
						style={{ width: 40, height: 40, borderRadius: 999, background: "#fff", border: `1px solid ${C.line}`, display: "flex", alignItems: "center", justifyContent: "center", color: "#7a716a", cursor: "pointer" }}>
						<CloseIcon />
					</button>
					<div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>{t("Pay with card")}</div>
					<div style={{ width: 40 }} />
				</div>

				<div style={{ padding: "8px 24px 48px", overflowY: "auto" }}>
					<div style={{ textAlign: "center", padding: "12px 0 20px" }}>
						<div style={{ fontSize: 14, color: C.muted }}>{businessName}</div>
						<div style={{ fontSize: 38, fontWeight: 800, color: C.ink, letterSpacing: -1, marginTop: 4 }}>{anticipationAmount || servicePrice}</div>
					</div>

					{clientSecret && stripePromise && (
						<Elements options={{ clientSecret, appearance: { theme: "stripe" } }} stripe={stripePromise}>
							<StripePaymentForm
								returnUrl={`${window.location.origin}${window.location.pathname}${window.location.search}`}
								onSuccess={() => {
									setStep("done");
									mixpanelTrackEvent('Pagamento_Posterior_Confirmado', {
										'Business_Id': businessId,
										'BookingTransaction_Id': bookingTransactionId,
										'Amount': resolvedPostPayment?.amount,
										'Currency': currency,
										'Tipo_Pagamento': 'cartao',
									}, {}, business || {});
								}}
								onError={(msg) => setPayError(msg)}
							/>
						</Elements>
					)}

					{payError && (
						<div style={{ color: C.red, fontSize: 13, marginTop: 16, textAlign: "center" }}>{payError}</div>
					)}

					<div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: C.muted, marginTop: 14, justifyContent: "center" }}>
						<LockIcon />
						{t("Secure and encrypted payment")}
					</div>
				</div>
			</FullScreen>

			{/* ── Full-screen PIX ── */}
			<FullScreen open={step === "pix"}>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px 4px" }}>
					<button
						onClick={() => { clearInterval(pollingRef.current); clearInterval(cdRef.current); setStep("initial"); }}
						style={{ width: 34, height: 34, borderRadius: 999, background: "#fff", border: `1px solid ${C.line}`, display: "flex", alignItems: "center", justifyContent: "center", color: "#7a716a", cursor: "pointer" }}>
						<CloseIcon />
					</button>
					<div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{t("Pix Payment")}</div>
					<div style={{ width: 34 }} />
				</div>

				<div style={{ padding: "2px 20px 16px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
					<div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
						{businessName}
					</div>
					<div style={{ fontSize: 28, fontWeight: 800, color: C.ink, letterSpacing: -1, marginTop: 2 }}>
						{anticipationAmount || servicePrice}
					</div>

					{/* Countdown */}
					<div style={{
						display: "inline-flex", alignItems: "center", gap: 6,
						fontSize: 12, fontWeight: 600,
						color: countdown <= 300 ? "#b3261e" : "#937f57",
						background: countdown <= 300 ? "#fbe6e4" : "#f3ead9",
						padding: "5px 12px", borderRadius: 999, marginTop: 8,
					}}>
						<TimerIcon />
						<span>{countdownLabel()}</span>
					</div>

					{/* QR Card */}
					<div style={{ marginTop: 10, background: "#fff", border: `1px solid ${C.line}`, borderRadius: 16, padding: 10, boxShadow: "0 8px 26px rgba(80,50,30,.09)" }}>
						<PixQR value={pixCode} />
					</div>

					{/* Copy code */}
					<div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color: C.muted, marginTop: 12, alignSelf: "flex-start" }}>
						{t("Pix copy and paste")}
					</div>
					<div style={{ marginTop: 6, width: "100%", background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, padding: "10px 12px", display: "flex", alignItems: "center", gap: 10 }}>
						<span style={{ flex: 1, minWidth: 0, fontFamily: '"SF Mono",ui-monospace,Menlo,Consolas,monospace', fontSize: 12, color: "#5a524b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "left" }}>
							{pixCode ? `${pixCode.substring(0, 40)}…` : t("Waiting for code")}
						</span>
						{pixCode && (
							<CopyToClipboard text={pixCode} onCopy={() => { setCopied(true); showToast(t("Pix code copied")); setTimeout(() => setCopied(false), 2000); }}>
								<button style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 10, background: "#f6efe8", border: "none", display: "flex", alignItems: "center", justifyContent: "center", color: C.wine, cursor: "pointer" }}>
									<CopyIcon />
								</button>
							</CopyToClipboard>
						)}
					</div>

					{pixCode && (
						<CopyToClipboard text={pixCode} onCopy={() => { setCopied(true); showToast(t("Pix code copied")); setTimeout(() => setCopied(false), 2000); }}>
							<button style={{ ...s.btnWine, height: 44, fontSize: 15, marginTop: 10 }}>
								{copied ? t("Copied") + "!" : t("Copy Pix code")}
							</button>
						</CopyToClipboard>
					)}

					{/* Waiting indicator */}
					<div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#f4e7e9", color: C.wine, fontWeight: 700, fontSize: 13, padding: "8px 16px", borderRadius: 999, marginTop: 12 }}>
						<PulseDot />
						{t("Waiting for payment")}
					</div>
					<div style={{ fontSize: 11.5, color: C.muted, marginTop: 6, maxWidth: 300, lineHeight: 1.35 }}>
						{t("Leave screen open for confirmation")}
					</div>

					{/* How to steps */}
					<div style={{ marginTop: 14, width: "100%", display: "flex", flexDirection: "column", gap: 8, textAlign: "left" }}>
						{[
							t("Pix step copy code"),
							t("Pix step open bank"),
							t("Pix step paste and confirm"),
						].map((text, i) => (
							<div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 12.5, color: "#5a524b", lineHeight: 1.35 }}>
								<div style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 999, background: C.wine, color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</div>
								{text}
							</div>
						))}
					</div>
				</div>
			</FullScreen>

			{/* ── Full-screen PIX Balcão (QR local, sem API) ── */}
			<FullScreen open={step === "pix_balcao"}>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px 4px" }}>
					<button
						onClick={() => setStep("initial")}
						style={{ width: 34, height: 34, borderRadius: 999, background: C.wine, border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
						<BackArrowIcon />
					</button>
					<div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{t("Pix Payment")}</div>
					<div style={{ width: 34 }} />
				</div>

				<div style={{ padding: "2px 20px 16px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", overflowY: "auto" }}>
					<div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{businessName}</div>
					<div style={{ fontSize: 28, fontWeight: 800, color: C.ink, letterSpacing: -1, marginTop: 2 }}>{anticipationAmount || servicePrice}</div>

					{/* QR Card */}
					<div style={{ marginTop: 10, background: "#fff", border: `1px solid ${C.line}`, borderRadius: 16, padding: 10, boxShadow: "0 8px 26px rgba(80,50,30,.09)" }}>
						<PixQR value={pixBalcaoCode} />
					</div>

					{/* Código copia e cola */}
					<div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color: C.muted, marginTop: 12, alignSelf: "flex-start" }}>
						{t("Pix copy and paste")}
					</div>
					<div style={{ marginTop: 6, width: "100%", background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, padding: "10px 12px", display: "flex", alignItems: "center", gap: 10 }}>
						<span style={{ flex: 1, minWidth: 0, fontFamily: '"SF Mono",ui-monospace,Menlo,Consolas,monospace', fontSize: 12, color: "#5a524b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "left" }}>
							{pixBalcaoCode ? `${pixBalcaoCode.substring(0, 40)}…` : ""}
						</span>
						{pixBalcaoCode && (
							<CopyToClipboard text={pixBalcaoCode} onCopy={() => { setCopied(true); showToast(t("Pix code copied")); setTimeout(() => setCopied(false), 2000); }}>
								<button style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 10, background: "#f6efe8", border: "none", display: "flex", alignItems: "center", justifyContent: "center", color: C.wine, cursor: "pointer" }}>
									<CopyIcon />
								</button>
							</CopyToClipboard>
						)}
					</div>

					{pixBalcaoCode && (
						<CopyToClipboard text={pixBalcaoCode} onCopy={() => { setCopied(true); showToast(t("Pix code copied")); setTimeout(() => setCopied(false), 2000); }}>
							<button style={{ ...s.btnWine, height: 44, fontSize: 15, marginTop: 10 }}>
								{copied ? t("Copied") + "!" : t("Copy Pix code")}
							</button>
						</CopyToClipboard>
					)}

					{/* Instruções */}
					<div style={{ marginTop: 14, width: "100%", display: "flex", flexDirection: "column", gap: 8, textAlign: "left" }}>
						{[
							t("Pix step copy code"),
							t("Pix step open bank"),
							"Confirme o pagamento e envie o comprovante ao estabelecimento",
						].map((text, i) => (
							<div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 12.5, color: "#5a524b", lineHeight: 1.35 }}>
								<div style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 999, background: C.wine, color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</div>
								{text}
							</div>
						))}
					</div>
				</div>
			</FullScreen>

			{/* ── Full-screen Done ── */}
			<FullScreen open={step === "done"}>
				<div style={{ minHeight: "calc(100% - 60px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "24px 32px" }}>
					<div style={{ width: 84, height: 84, borderRadius: 999, background: "#34c759", display: "flex", alignItems: "center", justifyContent: "center", margin: "4px auto 18px", animation: "lpop .4s cubic-bezier(.2,1.2,.4,1)" }}>
						<CheckIcon />
					</div>
					<div style={{ fontSize: 23, fontWeight: 800, color: C.ink }}>{t("Payment confirmed")}</div>
					<div style={{ fontSize: 16, color: C.muted, marginTop: 8 }}>
						{t("Appointment confirmed with business", { businessName: businessName || t("Professional") })}
					</div>
					<div style={{ fontSize: 17, color: C.muted, marginTop: 18 }}>
						<strong style={{ color: C.ink }}>{anticipationAmount || servicePrice}</strong>{" "}
						{isCardPritPay ? t("paid via card") : t("paid via Pix")}
					</div>
					<button
						onClick={() => history.push(`/business/${businessId || ""}`)}
						style={{ ...s.btnWine, marginTop: 26, maxWidth: 320 }}>
						{t("Finish")}
					</button>
				</div>
			</FullScreen>

			{/* ── Modal Pagamento já realizado ── */}
			{alreadyPaid && (
				<>
					<div
						style={{ position: "absolute", inset: 0, background: "rgba(40,28,22,.5)", zIndex: 90 }}
					/>
					<Modal onClose={null}>
						<div style={{ textAlign: "center" }}>
							<div style={{ width: 64, height: 64, borderRadius: 999, background: "#34c759", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
								<CheckIcon />
							</div>
							<div style={s.mTitle}>{t("Payment confirmed")}</div>
							<div style={{ ...s.mDesc, marginTop: 8 }}>
								{t("This payment has already been completed")}
							</div>
						</div>
						<div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 24 }}>
							<button
								onClick={() => history.push(`/business/${businessId || ""}`)}
								style={{ ...s.btnWine, marginTop: 0 }}>
								{t("New booking")}
							</button>
							<button
								onClick={() => history.push(`/reservas/${businessId || ""}`)}
								style={{ ...s.btnWine, marginTop: 0, background: "#fff", color: C.wine, border: `1.5px solid ${C.wine}` }}>
								{t("View reservations")}
							</button>
						</div>
					</Modal>
				</>
			)}

			{/* ── Toast ── */}
			{toastMsg && (
				<div style={{
					position: "absolute", left: "50%", bottom: 32,
					transform: "translateX(-50%)",
					background: "#2c2420", color: "#fff", fontSize: 14, fontWeight: 600,
					padding: "12px 20px", borderRadius: 999, zIndex: 200,
					whiteSpace: "nowrap",
				}}>
					{toastMsg}
				</div>
			)}

			<style>{`
				@keyframes lpop {
					0% { transform: scale(.4); opacity: 0; }
					100% { transform: scale(1); opacity: 1; }
				}
			`}</style>
		</div>
	);
};

// ─── layout helpers ────────────────────────────────────────────────────────────
const Modal = ({ children, onClose }) => (
	<div style={{
		position: "absolute", left: "50%", top: "50%",
		transform: "translate(-50%,-50%)",
		width: "calc(100% - 44px)", maxWidth: 380,
		background: "#fff", borderRadius: 24, padding: 24,
		zIndex: 100, boxShadow: "0 20px 60px rgba(40,28,22,.3)",
	}}>
		{children}
	</div>
);

const FullScreen = ({ open, children }) => (
	<div style={{
		position: "absolute", inset: 0,
		background: "#f7f0e9",
		zIndex: 120,
		transform: open ? "translateY(0)" : "translateY(100%)",
		transition: "transform .36s cubic-bezier(.22,.9,.33,1)",
		overflowY: "auto",
		WebkitOverflowScrolling: "touch",
	}}>
		{children}
	</div>
);

const PulseDot = () => (
	<span style={{ position: "relative", display: "inline-block", width: 10, height: 10, flexShrink: 0 }}>
		<span style={{ position: "absolute", inset: 0, borderRadius: 999, background: "#6b2030", animation: "lpulse 1.5s ease-out infinite" }} />
		<span style={{ display: "block", width: 10, height: 10, borderRadius: 999, background: "#6b2030" }} />
		<style>{`
			@keyframes lpulse {
				0% { transform: scale(1); opacity: .5; }
				100% { transform: scale(3.2); opacity: 0; }
			}
		`}</style>
	</span>
);

export default LaterPaymentCheckout;
